import React, { useState, useEffect, useRef, useCallback } from "react";
import { useAuth0 } from "@auth0/auth0-react";
import "../styles/components/partner.css";
import { PageLoader } from "../components/page-loader";
import livePrices from "../data/prices.live.json";
import testPrices from "../data/prices.test.json";
import EmbeddedCheckoutModal from "./EmbeddedCheckoutModal";

const priceData =
  process.env.REACT_APP_STRIPE_PRICES === "test" ? testPrices : livePrices;

const UserDomains = () => {
  const [domains, setDomains] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [accessToken, setAccessToken] = useState(null);
  const [userEmail, setUserEmail] = useState(null);
  const { isAuthenticated, getAccessTokenSilently, user } = useAuth0();
  const API_URL = process.env.REACT_APP_API_BASE_URL;
  const [showSettingsMenu, setShowSettingsMenu] = useState({});
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(null);
  const [showUpgradePopup, setShowUpgradePopup] = useState(null);
  const [showUpgradeConfirmation, setShowUpgradeConfirmation] = useState(null);
  const [selectedPackage, setSelectedPackage] = useState(null);
  const [billingCycle, setBillingCycle] = useState("monthly"); // "monthly" or "annual"
  const isDeletingRef = useRef(false);
  const [deletingDomains, setDeletingDomains] = useState({});
  const [upgradingDomains, setUpgradingDomains] = useState({});
  const [previewPrice, setPreviewPrice] = useState(null);
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [isAdminOrPartner, setIsAdminOrPartner] = useState(false);
  const [bypassPayment, setBypassPayment] = useState(false);
  const [showStripeCheckout, setShowStripeCheckout] = useState(false);
  const [stripeCheckoutPackage, setStripeCheckoutPackage] = useState(null);

  // Package definitions
  const packages = priceData.upgradePackages;

  useEffect(() => {
    if (isAuthenticated) {
      getAccessTokenSilently().then((token) => {
        setAccessToken(token);
      });

      if (user && user.email) {
        setUserEmail(user.email);
      }
    }
  }, [isAuthenticated, getAccessTokenSilently, user]);

  const fetchDomains = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/user-domains-test`, {
        headers: {
          Authorization: `Bearer ${accessToken}`,
          "X-User-Email": userEmail,
        },
      });

      if (!response.ok) {
        throw new Error("Failed to fetch domains");
      }

      const data = await response.json();
      setDomains(data.domains);
      setIsAdminOrPartner(!!(data.isAdmin || data.isPartner));
      setLoading(false);
    } catch (error) {
      setError(`Error: ${error.message}`);
      setLoading(false);
      console.error("Error:", error);
    }
  }, [accessToken, userEmail, API_URL]);

  useEffect(() => {
    if (accessToken && userEmail) {
      fetchDomains();
    }
  }, [accessToken, userEmail, fetchDomains]);

  const getMaxRequestCount = (request_size) => {
    switch (request_size) {
      case "packone":
        return "10 000";
      case "packtwo":
        return "500 000";
      case "packthree":
        return "1 000 000";
      case "packfour":
        return "5 000 000";
      case "packfive":
        return "10 000 000";
      default:
        return "Unknown";
    }
  };

  const toggleSettingsMenu = (domain) => {
    setShowSettingsMenu((prev) => ({
      ...prev,
      [domain]: !prev[domain],
    }));
  };

  const showDeleteConfirmationPopup = (domain) => {
    setShowDeleteConfirmation(domain);
    setShowSettingsMenu((prev) => ({ ...prev, [domain]: false }));
  };

  const showUpgradePopupMenu = (domain) => {
    setShowUpgradePopup(domain);
    setShowSettingsMenu((prev) => ({ ...prev, [domain]: false }));
    setSelectedPackage(null);
    setBillingCycle("monthly");
    setPreviewPrice(null);
    setBypassPayment(false);
  };

  const showUpgradeConfirmationPopup = async (domain, pkg) => {
    // Free-tier users without admin/partner role need a subscription first
    const domainData = domains.find((d) => d.domain === domain);
    if (domainData?.request_size === "packone" && !isAdminOrPartner) {
      // Check if they already have an unallocated subscription for this package
      try {
        const response = await fetch(`${API_URL}/subscriptions`, {
          headers: {
            Authorization: `Bearer ${accessToken}`,
            "X-User-Email": userEmail,
          },
        });
        const { subscriptions } = await response.json();
        const hasUnallocated = subscriptions?.some(
          (s) => s.package_name === pkg.package_name && s.allocated === 0
        );

        if (!hasUnallocated) {
          // No unallocated sub - open Stripe embed with the correct price
          setShowUpgradePopup(null);
          setStripeCheckoutPackage({ ...pkg, packname: pkg.package_name });
          setShowStripeCheckout(domain);
          return;
        }
        // Has an unallocated sub - show confirmation popup, no price preview
        // (the unallocated sub has domain: null so preview-upgrade can't find it)
        setSelectedPackage(pkg);
        setShowUpgradePopup(null);
        setShowUpgradeConfirmation(domain);
        setBypassPayment(true);
        setPreviewPrice(null);
        setLoadingPreview(false);
        return;
      } catch (error) {
        console.error("Error checking subscriptions:", error);
        return;
      }
    }

    setSelectedPackage(pkg);
    setShowUpgradePopup(null);
    setShowUpgradeConfirmation(domain);

    // Admin/partner users skip payment - no price preview needed
    if (isAdminOrPartner) {
      setBypassPayment(true);
      setPreviewPrice(null);
      setLoadingPreview(false);
      return;
    }

    setLoadingPreview(true);

    try {
      const price_id =
        billingCycle === "monthly"
          ? pkg.stripeMonthlyPriceId
          : pkg.stripeAnnualPriceId;

      const response = await fetch(
        `${API_URL}/subscriptions/preview-upgrade/${domain}`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${accessToken}`,
            "X-User-Email": userEmail,
          },
          body: JSON.stringify({
            price_id: price_id,
          }),
        }
      );

      if (!response.ok) {
        throw new Error("Failed to preview upgrade price");
      }

      const data = await response.json();

      if (data.success) {
        // Convert amount from øre to kroner
        setPreviewPrice(data.preview / 100);
      } else {
        throw new Error(data.error || "Preview failed");
      }
    } catch (error) {
      setError(`Error fetching price: ${error.message}`);
      console.error("Error:", error);
      setPreviewPrice(null);
    } finally {
      setLoadingPreview(false);
    }
  };

  const upgradeSubscription = async (domain) => {
    if (!accessToken || !userEmail || !selectedPackage) return;

    try {
      setUpgradingDomains((prev) => ({ ...prev, [domain]: true }));

      const price_id =
        billingCycle === "monthly"
          ? selectedPackage.stripeMonthlyPriceId
          : selectedPackage.stripeAnnualPriceId;

      const response = await fetch(
        `${API_URL}/subscriptions/upgrade/${domain}`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${accessToken}`,
            "X-User-Email": userEmail,
          },
          body: JSON.stringify({
            package_name: selectedPackage.package_name,
            title: selectedPackage.title,
            price_id: price_id,
          }),
        }
      );

      if (!response.ok) {
        throw new Error("Failed to upgrade subscription");
      }

      const result = await response.json();

      if (result.requiresPayment) {
        // No active subscription - redirect to pricing page
        setShowUpgradeConfirmation(null);
        window.location.href = "/priser";
        return;
      }

      if (result.success) {
        // Update the domain with new package information
        setDomains((prev) =>
          prev.map((d) =>
            d.domain === domain
              ? {
                  ...d,
                  request_size: selectedPackage.package_name,
                  title: selectedPackage.title,
                }
              : d
          )
        );
        setShowUpgradeConfirmation(null);
        setSelectedPackage(null);
        setPreviewPrice(null);
        setBypassPayment(false);
      } else {
        throw new Error(result.message || "Upgrade failed");
      }
    } catch (error) {
      setError(`Error: ${error.message}`);
      console.error("Error:", error);
    } finally {
      setUpgradingDomains((prev) => ({ ...prev, [domain]: false }));
    }
  };

  const deleteDomain = async (domain) => {
    if (isDeletingRef.current || !accessToken || !userEmail) return;
    isDeletingRef.current = true;

    try {
      setDeletingDomains((prev) => ({ ...prev, [domain]: true }));

      const response = await fetch(`${API_URL}/run-script2`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${accessToken}`,
          "X-User-Email": userEmail,
        },
        body: JSON.stringify({ domain }),
      });

      if (!response.ok) {
        throw new Error("Failed to delete domain");
      }

      const { jobId } = await response.json();
      await pollJobStatus(jobId, domain);
    } catch (error) {
      setError(`Error: ${error.message}`);
      console.error("Error:", error);
    } finally {
      isDeletingRef.current = false;
    }
  };

  const pollJobStatus = async (jobId, domain) => {
    try {
      const interval = setInterval(async () => {
        const response = await fetch(`${API_URL}/job-status/${jobId}`, {
          headers: {
            Authorization: `Bearer ${accessToken}`,
            "X-User-Email": userEmail,
          },
        });

        if (!response.ok) {
          throw new Error("Failed to check job status");
        }

        const { status, error } = await response.json();

        if (status === "completed") {
          setDomains((prev) =>
            prev.map((d) => (d.domain === domain ? { ...d, deleted: true } : d))
          );
          clearInterval(interval);
          setDeletingDomains((prev) => ({ ...prev, [domain]: false }));
          setShowDeleteConfirmation(null);
          window.location.reload();
        } else if (status === "failed") {
          throw new Error(error || "Failed to delete domain");
        }
      }, 2000);
    } catch (error) {
      setError(`Error: ${error.message}`);
      console.error("Error:", error);
      setDeletingDomains((prev) => ({ ...prev, [domain]: false }));
    }
  };

  const formatNumber = (num) => {
    return num.toString().replace(/\B(?=(\d{3})+(?!\d))/g, " ");
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat("nb-NO", {
      style: "currency",
      currency: "NOK",
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(price);
  };

  return (
    <div className="user-domains">
      {showStripeCheckout && stripeCheckoutPackage && (
        <EmbeddedCheckoutModal
          package={stripeCheckoutPackage}
          billingCycle={billingCycle}
          domain={showStripeCheckout}
          onClose={() => {
            setShowStripeCheckout(false);
            setStripeCheckoutPackage(null);
          }}
        />
      )}
      <h2>Dine servere:</h2>
      {loading ? (
        <PageLoader />
      ) : error ? (
        <p className="error">{error}</p>
      ) : domains.length === 0 ? (
        <p>Du har ingen domener.</p>
      ) : (
        <ul>
          {domains.map(
            (
              {
                domain,
                status,
                requestCount,
                request_size,
                partner_emails,
                isOwner,
              },
              index
            ) => (
              <li key={index}>
                {domain}
                <span className={`status ${status}`}>{status}</span>
                <span className="request-count">
                  Forespørsler:{" "}
                  {requestCount !== undefined && request_size !== undefined
                    ? `${requestCount}/${getMaxRequestCount(request_size)}`
                    : "Loading..."}
                </span>
                {isOwner && (
                  <div className="settings-menu">
                    <button
                      className="settings-button"
                      onClick={() => toggleSettingsMenu(domain)}
                    >
                      &#8942;
                    </button>
                    {showSettingsMenu[domain] && (
                      <div className="settings-dropdown">
                        <button onClick={() => showUpgradePopupMenu(domain)}>
                          Oppgrader
                        </button>
                        <button
                          onClick={() => showDeleteConfirmationPopup(domain)}
                          disabled={deletingDomains[domain]}
                        >
                          Slett
                        </button>
                      </div>
                    )}
                  </div>
                )}
                {showDeleteConfirmation === domain && (
                  <div className="delete-confirmation-popup">
                    <p>Er du sikker på at du vil slette {domain}?</p>
                    <button
                      onClick={() => deleteDomain(domain)}
                      disabled={deletingDomains[domain]}
                    >
                      {deletingDomains[domain] ? "Sletter..." : "Ja"}
                    </button>
                    <button onClick={() => setShowDeleteConfirmation(null)}>
                      Nei
                    </button>
                  </div>
                )}
                {showUpgradePopup === domain && (
                  <div className="upgrade-popup">
                    <h3>Oppgrader pakke for {domain}</h3>

                    {/* Billing Cycle Toggle */}
                    <div className="billing-cycle-toggle">
                      <button
                        className={billingCycle === "monthly" ? "active" : ""}
                        onClick={() => setBillingCycle("monthly")}
                      >
                        Månedlig
                      </button>
                      <button
                        className={billingCycle === "annual" ? "active" : ""}
                        onClick={() => setBillingCycle("annual")}
                      >
                        Årlig
                      </button>
                    </div>

                    {/* Package Options */}
                    <div className="package-options">
                      {packages.map((pkg, index) => (
                        <div
                          key={index}
                          className={`package-option ${
                            selectedPackage?.package_name === pkg.package_name
                              ? "selected"
                              : ""
                          }`}
                          onClick={() => setSelectedPackage(pkg)}
                        >
                          <h4>{pkg.title}</h4>
                          <p>{formatNumber(pkg.requests)} forespørsler</p>
                          <button
                            onClick={() =>
                              showUpgradeConfirmationPopup(domain, pkg)
                            }
                          >
                            Velg
                          </button>
                        </div>
                      ))}
                    </div>

                    <button
                      onClick={() => setShowUpgradePopup(null)}
                      className="close-button"
                    >
                      Lukk
                    </button>
                  </div>
                )}
                {showUpgradeConfirmation === domain && (
                  <div className="upgrade-confirmation-popup">
                    <p>Er du sikker på at du vil oppgradere pakken?</p>
                    {selectedPackage && (
                      <>
                        <p className="package-details">
                          Ny pakke: {selectedPackage.title} (
                          {formatNumber(selectedPackage.requests)} forespørsler)
                        </p>
                        {!bypassPayment && (
                          <p className="price-details">
                            {loadingPreview ? (
                              "Henter pris..."
                            ) : previewPrice !== null ? (
                              <>
                                Du vil bli belastet:{" "}
                                <strong>{formatPrice(previewPrice)}</strong>
                              </>
                            ) : (
                              "Kunne ikke hente pris"
                            )}
                          </p>
                        )}
                      </>
                    )}
                    <button
                      onClick={() => upgradeSubscription(domain)}
                      disabled={
                        upgradingDomains[domain] ||
                        loadingPreview ||
                        (!bypassPayment && previewPrice === null)
                      }
                    >
                      {upgradingDomains[domain] ? "Oppgraderer..." : "Ja"}
                    </button>
                    <button
                      onClick={() => {
                        setShowUpgradeConfirmation(null);
                        setPreviewPrice(null);
                        setBypassPayment(false);
                      }}
                      disabled={upgradingDomains[domain]}
                    >
                      Nei
                    </button>
                  </div>
                )}
              </li>
            )
          )}
        </ul>
      )}
    </div>
  );
};

export default UserDomains;
